import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { Maximize2, ZoomIn, ZoomOut } from 'lucide-react';
import {
  clampOpeningPosition,
  flattenOutline,
  moveShapeVertex,
  insertShapeVertex,
  openingGeometry,
  pointOnWall,
  pointsBounds,
  projectOntoCurvedWall,
  snapToGridFt,
  snapWallAngle,
  wallAt,
  wallLength,
  wallPoints,
  type ShapePoint,
} from '@peles/shared';
import { RoomShapeLayer } from './RoomShapeLayer';
import type { RoomShapeEditorState } from './useRoomShapeEditor';

/** SVG units per foot. The view box is in these, so strokes read the same at any zoom. */
export const UNITS_PER_FT = 10;
const MARGIN_FT = 6;

export type CanvasMode = 'edit' | 'trace' | 'calibrate';

export interface CanvasBackground {
  url: string;
  x: number;
  y: number;
  feetPerPixel: number;
  opacity: number;
  pixelWidth: number;
  pixelHeight: number;
}

export interface SnapSettings {
  /** Grid step in feet; 0 turns the grid snap off. */
  gridFt: number;
  /** Keep walls level or plumb while drawing and dragging. */
  rightAngles: boolean;
}

interface RoomShapeCanvasProps {
  editor: RoomShapeEditorState;
  mode: CanvasMode;
  snap: SnapSettings;
  background?: CanvasBackground | null;
  /** Corners placed so far while tracing. */
  tracePoints?: ShapePoint[];
  onTracePoint?: (p: ShapePoint) => void;
  /** Clicking the first traced corner again closes the outline. */
  onTraceClose?: () => void;
  calibrationPoints?: ShapePoint[];
  onCalibrationPoint?: (p: ShapePoint) => void;
}

/**
 * Where on the drawing a pointer is, in feet. Uses the SVG's own transform
 * when the browser provides one, and the box and view box otherwise (jsdom).
 */
export function eventToFeet(
  svg: SVGSVGElement,
  clientX: number,
  clientY: number,
  viewBox: { x: number; y: number; w: number; h: number },
): ShapePoint {
  const ctm = svg.getScreenCTM?.();
  if (ctm && typeof svg.createSVGPoint === 'function') {
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const local = pt.matrixTransform(ctm.inverse());
    return { x: local.x / UNITS_PER_FT, y: local.y / UNITS_PER_FT };
  }
  const rect = svg.getBoundingClientRect();
  const sx = rect.width ? viewBox.w / rect.width : 1;
  const sy = rect.height ? viewBox.h / rect.height : 1;
  return {
    x: (viewBox.x + (clientX - rect.left) * sx) / UNITS_PER_FT,
    y: (viewBox.y + (clientY - rect.top) * sy) / UNITS_PER_FT,
  };
}

/**
 * Pull a dragged corner level with, or plumb under, its neighbours when it
 * is within `toleranceFt` — the drag equivalent of the right-angle snap.
 */
export function alignToNeighbours(
  p: ShapePoint,
  neighbours: ShapePoint[],
  toleranceFt = 1,
): ShapePoint {
  let { x, y } = p;
  for (const n of neighbours) {
    if (Math.abs(x - n.x) <= toleranceFt) x = n.x;
    if (Math.abs(y - n.y) <= toleranceFt) y = n.y;
  }
  return { x, y };
}

type Drag =
  | { kind: 'vertex'; index: number }
  | { kind: 'feature'; id: string; dx: number; dy: number }
  | { kind: 'opening'; id: string; segment: number }
  | { kind: 'pan'; startX: number; startY: number; origin: { x: number; y: number } };

export function RoomShapeCanvas({
  editor,
  mode,
  snap,
  background,
  tracePoints = [],
  onTracePoint,
  onTraceClose,
  calibrationPoints = [],
  onCalibrationPoint,
}: RoomShapeCanvasProps) {
  const { shape, selection, setSelection, update, endGesture, updateFeature, updateOpening } =
    editor;
  const svgRef = useRef<SVGSVGElement>(null);
  const drag = useRef<Drag | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [hover, setHover] = useState<ShapePoint | null>(null);

  // Fit the room, its features and the traced drawing, with a margin.
  const fit = useMemo(() => {
    const pts: ShapePoint[] = [...flattenOutline(shape.outline), ...tracePoints];
    shape.features.forEach((f) =>
      pts.push({ x: f.x, y: f.y }, { x: f.x + f.widthFt, y: f.y + f.heightFt }),
    );
    if (background) {
      pts.push(
        { x: background.x, y: background.y },
        {
          x: background.x + background.pixelWidth * background.feetPerPixel,
          y: background.y + background.pixelHeight * background.feetPerPixel,
        },
      );
    }
    const b = pts.length ? pointsBounds(pts) : { minX: 0, minY: 0, width: 40, height: 30 };
    return {
      x: (b.minX - MARGIN_FT) * UNITS_PER_FT,
      y: (b.minY - MARGIN_FT) * UNITS_PER_FT,
      w: (Math.max(b.width, 10) + MARGIN_FT * 2) * UNITS_PER_FT,
      h: (Math.max(b.height, 10) + MARGIN_FT * 2) * UNITS_PER_FT,
    };
  }, [shape, tracePoints, background]);

  const viewBox = useMemo(() => {
    const w = fit.w / zoom;
    const h = fit.h / zoom;
    return { x: fit.x + (fit.w - w) / 2 + pan.x, y: fit.y + (fit.h - h) / 2 + pan.y, w, h };
  }, [fit, zoom, pan]);

  const toFeet = useCallback(
    (e: { clientX: number; clientY: number }) =>
      svgRef.current ? eventToFeet(svgRef.current, e.clientX, e.clientY, viewBox) : { x: 0, y: 0 },
    [viewBox],
  );

  const snapPoint = useCallback((p: ShapePoint) => snapToGridFt(p, snap.gridFt), [snap.gridFt]);

  /** The next traced corner, snapped to the grid and to a right angle from the last. */
  const traceTarget = useCallback(
    (raw: ShapePoint) => {
      const last = tracePoints[tracePoints.length - 1];
      const p = last && snap.rightAngles ? snapWallAngle(last, raw, 90) : raw;
      return snapPoint(p);
    },
    [tracePoints, snap.rightAngles, snapPoint],
  );

  const onPointerMove = (e: ReactPointerEvent<SVGSVGElement>) => {
    const p = toFeet(e);
    if (mode === 'trace') setHover(traceTarget(p));
    const d = drag.current;
    if (!d) return;
    if (d.kind === 'pan') {
      // The drawing keeps its proportions in the element (`xMidYMid meet`), so
      // one screen pixel is the larger of the two view-box scales.
      const rect = svgRef.current?.getBoundingClientRect();
      const k =
        rect?.width && rect.height ? Math.max(viewBox.w / rect.width, viewBox.h / rect.height) : 1;
      setPan({
        x: d.origin.x - (e.clientX - d.startX) * k,
        y: d.origin.y - (e.clientY - d.startY) * k,
      });
      return;
    }
    if (d.kind === 'vertex') {
      const n = shape.outline.length;
      const neighbours = [shape.outline[(d.index - 1 + n) % n], shape.outline[(d.index + 1) % n]];
      let target = snapPoint(p);
      if (snap.rightAngles) target = alignToNeighbours(target, neighbours);
      update((s) => moveShapeVertex(s, d.index, target), { coalesce: true });
    } else if (d.kind === 'feature') {
      const target = snapPoint({ x: p.x - d.dx, y: p.y - d.dy });
      updateFeature(d.id, target, { coalesce: true });
    } else if (d.kind === 'opening') {
      const { a, b, bulge } = wallAt(shape.outline, d.segment);
      const width = shape.openings.find((o) => o.id === d.id)?.widthFt ?? 0;
      // Stop at the corner: the whole door stays on its wall.
      // Along the curve for a curved wall; rounded first so the clamp has the last word.
      const raw = Math.round(projectOntoCurvedWall(a, b, bulge, p) * 1000) / 1000;
      const t = clampOpeningPosition(wallLength(a, b, bulge), width, raw);
      updateOpening(d.id, { position: t }, { coalesce: true });
    }
  };

  const onPointerUp = () => {
    if (drag.current && drag.current.kind !== 'pan') endGesture();
    drag.current = null;
  };

  const startDrag = (e: ReactPointerEvent, d: Drag) => {
    e.stopPropagation();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    drag.current = d;
  };

  const onBackgroundPointerDown = (e: ReactPointerEvent<SVGSVGElement>) => {
    const p = toFeet(e);
    if (mode === 'trace') {
      const first = tracePoints[0];
      if (
        first &&
        tracePoints.length >= 3 &&
        Math.hypot(p.x - first.x, p.y - first.y) < 1.5 * (1 / zoom) + 0.5
      ) {
        onTraceClose?.();
      } else {
        onTracePoint?.(traceTarget(p));
      }
      return;
    }
    if (mode === 'calibrate') {
      onCalibrationPoint?.(p);
      return;
    }
    setSelection(null);
    (e.target as Element).setPointerCapture?.(e.pointerId);
    drag.current = { kind: 'pan', startX: e.clientX, startY: e.clientY, origin: pan };
  };

  const onWheel = (e: React.WheelEvent) => {
    setZoom((z) => Math.min(20, Math.max(0.25, z * (e.deltaY < 0 ? 1.15 : 1 / 1.15))));
  };

  const u = UNITS_PER_FT;
  const handleR = Math.max(3, 6 / zoom);
  const editing = mode === 'edit';
  const selectedWall = selection?.type === 'wall' ? selection.index : -1;

  return (
    <div className="relative h-full w-full" data-testid="room-shape-canvas">
      <svg
        ref={svgRef}
        className={`h-full w-full touch-none select-none bg-white ${mode === 'edit' ? 'cursor-grab' : 'cursor-crosshair'}`}
        viewBox={`${viewBox.x} ${viewBox.y} ${viewBox.w} ${viewBox.h}`}
        onPointerDown={onBackgroundPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={() => setHover(null)}
        onWheel={onWheel}
        role="application"
        aria-label="Room shape drawing"
      >
        <defs>
          <pattern id="room-grid" width={u} height={u} patternUnits="userSpaceOnUse">
            <path d={`M ${u} 0 L 0 0 0 ${u}`} fill="none" stroke="#f1f5f9" strokeWidth={0.5} />
          </pattern>
          <pattern id="room-grid-5" width={u * 5} height={u * 5} patternUnits="userSpaceOnUse">
            <rect width={u * 5} height={u * 5} fill="url(#room-grid)" />
            <path
              d={`M ${u * 5} 0 L 0 0 0 ${u * 5}`}
              fill="none"
              stroke="#e2e8f0"
              strokeWidth={0.8}
            />
          </pattern>
        </defs>
        <rect
          x={viewBox.x}
          y={viewBox.y}
          width={viewBox.w}
          height={viewBox.h}
          fill="url(#room-grid-5)"
        />

        {background && (
          <image
            href={background.url}
            x={background.x * u}
            y={background.y * u}
            width={background.pixelWidth * background.feetPerPixel * u}
            height={background.pixelHeight * background.feetPerPixel * u}
            opacity={background.opacity}
            preserveAspectRatio="none"
            pointerEvents="none"
            data-testid="room-shape-background"
          />
        )}

        {shape.outline.length >= 2 && (
          <>
            <path
              d={`${flattenOutline(shape.outline)
                .map((p, i) => `${i ? 'L' : 'M'}${p.x * u},${p.y * u}`)
                .join(' ')} Z`}
              fill="#eef2ff"
              fillOpacity={background ? 0.35 : 0.6}
              pointerEvents="none"
            />
            <RoomShapeLayer shape={shape} scale={u} floorColor="#eef2ff" />
          </>
        )}

        {editing &&
          shape.outline.map((_, i) => {
            const { a, b, bulge } = wallAt(shape.outline, i);
            const mid = pointOnWall(a, b, bulge, 0.5).point;
            const selected = selectedWall === i;
            return (
              <g key={`wall-${i}`}>
                {/* The wall as drawn — along its curve for a curved one — so
                    clicking what is on screen selects it. A straight line
                    between the corners left a curved wall unclickable.
                    `wallPoints` leaves the end corner to the next wall. */}
                <polyline
                  points={[...wallPoints(a, b, bulge), b]
                    .map((p) => `${p.x * u},${p.y * u}`)
                    .join(' ')}
                  fill="none"
                  stroke={selected ? '#7c3aed' : 'transparent'}
                  strokeWidth={selected ? 4 : 12 / zoom}
                  className="cursor-pointer"
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    setSelection({ type: 'wall', index: i });
                  }}
                  data-testid={`room-wall-${i}`}
                />
                <g
                  className="cursor-copy"
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    update((s) => insertShapeVertex(s, i));
                    setSelection({ type: 'vertex', index: i + 1 });
                  }}
                  data-testid={`room-wall-add-${i}`}
                >
                  <circle
                    cx={mid.x * u}
                    cy={mid.y * u}
                    r={handleR * 0.8}
                    fill="white"
                    stroke="#a78bfa"
                    strokeWidth={1}
                  />
                  <path
                    d={`M${mid.x * u - handleR * 0.45},${mid.y * u} h${handleR * 0.9} M${mid.x * u},${mid.y * u - handleR * 0.45} v${handleR * 0.9}`}
                    stroke="#7c3aed"
                    strokeWidth={1}
                  />
                </g>
              </g>
            );
          })}

        {editing &&
          shape.features.map((f) => (
            <rect
              key={`fh-${f.id}`}
              x={f.x * u}
              y={f.y * u}
              width={f.widthFt * u}
              height={f.heightFt * u}
              fill="transparent"
              stroke={
                selection?.type === 'feature' && selection.id === f.id ? '#7c3aed' : 'transparent'
              }
              strokeWidth={2}
              className="cursor-move"
              onPointerDown={(e) => {
                const p = toFeet(e);
                setSelection({ type: 'feature', id: f.id });
                startDrag(e, { kind: 'feature', id: f.id, dx: p.x - f.x, dy: p.y - f.y });
              }}
              data-testid={`room-feature-handle-${f.id}`}
            />
          ))}

        {editing &&
          shape.openings
            .filter((o) => o.segment < shape.outline.length)
            .map((o) => {
              const g = openingGeometry(shape, o);
              const selected = selection?.type === 'opening' && selection.id === o.id;
              return (
                <line
                  key={`oh-${o.id}`}
                  x1={g.start.x * u}
                  y1={g.start.y * u}
                  x2={g.end.x * u}
                  y2={g.end.y * u}
                  stroke={selected ? '#7c3aed' : 'transparent'}
                  strokeWidth={10 / zoom}
                  strokeOpacity={0.5}
                  className="cursor-ew-resize"
                  onPointerDown={(e) => {
                    setSelection({ type: 'opening', id: o.id });
                    startDrag(e, { kind: 'opening', id: o.id, segment: o.segment });
                  }}
                  data-testid={`room-opening-handle-${o.id}`}
                />
              );
            })}

        {editing &&
          shape.outline.map((p, i) => {
            const selected = selection?.type === 'vertex' && selection.index === i;
            return (
              <circle
                key={`v-${i}`}
                cx={p.x * u}
                cy={p.y * u}
                r={handleR}
                fill={selected ? '#7c3aed' : 'white'}
                stroke="#7c3aed"
                strokeWidth={1.5}
                className="cursor-move"
                onPointerDown={(e) => {
                  setSelection({ type: 'vertex', index: i });
                  startDrag(e, { kind: 'vertex', index: i });
                }}
                data-testid={`room-vertex-${i}`}
              />
            );
          })}

        {mode === 'trace' && tracePoints.length > 0 && (
          <g pointerEvents="none" data-testid="room-trace">
            <polyline
              points={[...tracePoints, ...(hover ? [hover] : [])]
                .map((p) => `${p.x * u},${p.y * u}`)
                .join(' ')}
              fill="none"
              stroke="#7c3aed"
              strokeWidth={2}
              strokeDasharray="6 3"
            />
            {tracePoints.map((p, i) => (
              <circle
                key={i}
                cx={p.x * u}
                cy={p.y * u}
                r={i === 0 && tracePoints.length >= 3 ? handleR * 1.4 : handleR}
                fill={i === 0 ? '#7c3aed' : 'white'}
                stroke="#7c3aed"
              />
            ))}
          </g>
        )}

        {mode === 'calibrate' &&
          calibrationPoints.map((p, i) => (
            <g key={i} pointerEvents="none">
              <circle cx={p.x * u} cy={p.y * u} r={handleR} fill="#f59e0b" />
              {i === 1 && (
                <line
                  x1={calibrationPoints[0].x * u}
                  y1={calibrationPoints[0].y * u}
                  x2={p.x * u}
                  y2={p.y * u}
                  stroke="#f59e0b"
                  strokeWidth={2}
                />
              )}
            </g>
          ))}
      </svg>

      <div className="absolute right-2 top-2 flex flex-col gap-1 rounded-md bg-white/90 p-1 shadow">
        <button
          type="button"
          aria-label="Zoom in"
          className="p-1 text-gray-600 hover:text-gray-900"
          onClick={() => setZoom((z) => Math.min(20, z * 1.25))}
        >
          <ZoomIn size={16} />
        </button>
        <button
          type="button"
          aria-label="Zoom out"
          className="p-1 text-gray-600 hover:text-gray-900"
          onClick={() => setZoom((z) => Math.max(0.25, z / 1.25))}
        >
          <ZoomOut size={16} />
        </button>
        <button
          type="button"
          aria-label="Fit to view"
          className="p-1 text-gray-600 hover:text-gray-900"
          onClick={() => {
            setZoom(1);
            setPan({ x: 0, y: 0 });
          }}
        >
          <Maximize2 size={16} />
        </button>
      </div>
    </div>
  );
}
